import _extends from "@babel/runtime/helpers/esm/extends";
import _objectWithoutProperties from "@babel/runtime/helpers/esm/objectWithoutProperties";
import _defineProperty from "@babel/runtime/helpers/esm/defineProperty";

function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); if (enumerableOnly) symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; }); keys.push.apply(keys, symbols); } return keys; }

function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i] != null ? arguments[i] : {}; if (i % 2) { ownKeys(Object(source), true).forEach(function (key) { _defineProperty(target, key, source[key]); }); } else if (Object.getOwnPropertyDescriptors) { Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)); } else { ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } } return target; }

import React, { forwardRef, useCallback, useState, useRef } from 'react';
import { createStyles, makeStyles } from '@material-ui/core/styles';
import Select, { createFilter } from 'react-select';
import CreatableSelect from 'react-select/creatable';
import { FormattedMessage } from 'react-intl';
import { Control, DropdownIndicator, Menu, MenuList, NoOptionsMessage, Option, Placeholder, SingleValue, SuggestProvider, ValueContainer } from './components';
import messages from './messages';
const selectorStyles = {
  indicatorSeparator: () => ({
    display: 'none'
  }),
  input: provided => _objectSpread(_objectSpread({}, provided), {}, {
    position: 'absolute'
  })
};
const useStyles = makeStyles(theme => createStyles({
  root: {
    maxWidth: '100%',
    '&& span.suggest-box__indicator-separator': {
      display: 'none'
    }
  },
  input: props => _objectSpread(_objectSpread({
    flex: 1,
    minWidth: '3rem',
    width: 'fit-content',
    display: 'flex',
    overflow: 'hidden'
  }, props.disabled ? {} : {
    paddingRight: 24
  }), {}, {
    '& svg': {
      position: 'absolute',
      top: 'calc(50% - 12px)',
      right: 0
    },
    '& svg path': {
      stroke: 'none'
    }
  }),
  inputBase: props => _objectSpread({}, props.disabled ? {
    '&&:before': {
      borderBottom: 0
    }
  } : {}),
  valueContainer: props => _objectSpread(_objectSpread({
    display: 'flex',
    flexWrap: 'wrap',
    maxWidth: '100%',
    marginRight: 2
  }, props.fitContent ? {
    width: 'fit-content',
    maxHeight: '3em'
  } : {
    flex: 1
  }), {}, {
    '& > div, & .suggest-box__input': {
      width: 'fit-content',
      fontSize: '1rem',
      margin: 0,
      color: theme.palette.text.hint
    },
    '& > div': {
      overflow: 'hidden'
    }
  }),
  noOptionsMessage: {
    minWidth: 'max-content',
    padding: theme.spacing(1, 2)
  },
  singleValue: {
    width: 'fit-content',
    // 2px is margin-right
    maxWidth: 'calc(100% + 2px)%',
    position: 'relative',
    marginRight: -2,
    fontSize: '1rem',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis'
  },
  placeholder: props => _objectSpread(_objectSpread({
    position: 'absolute',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    width: '95%',
    left: 2,
    bottom: 6
  }, props.hasError ? {
    color: theme.palette.error.main
  } : ''), props.smallSize ? {
    fontSize: 14
  } : {
    fontSize: 15
  }),
  paper: props => _objectSpread(_objectSpread({
    position: 'absolute',
    zIndex: 1,
    marginTop: 0,
    left: 0
  }, props.fitContent ? {} : {
    right: 0
  }), theme.props.isSafari ? {
    minWidth: 'max-content',
    '& > div': {
      minWidth: 'max-content'
    }
  } : {}),
  divider: {
    height: theme.spacing(2)
  }
}));
const components = {
  Control,
  DropdownIndicator,
  Menu,
  MenuList,
  NoOptionsMessage,
  Option,
  Placeholder,
  SingleValue,
  ValueContainer
};
export const SuggestBox = (_ref) => {
  let {
    creatable,
    disabled,
    value,
    inputId = 'react-select-single',
    handleChange,
    options,
    placeholder,
    tooltipTitle
  } = _ref,
      rest = _objectWithoutProperties(_ref, ["creatable", "disabled", "value", "inputId", "handleChange", "options", "placeholder", "tooltipTitle"]);

  const temp = useRef(null);
  const classes = useStyles(_objectSpread({
    disabled
  }, rest));
  const [onChangeHandler, setOnChangeHandler] = useState({
    onChange(val, action) {
      if (typeof handleChange === 'function') {
        return handleChange(val, action);
      }

      return undefined;
    }

  });
  const updateOnChangeHandler = useCallback(cb => {
    const newHandler = cb(); // The Provider would need to be mocked to test this...

    /* istanbul ignore else */

    if (typeof newHandler.onChange === 'function') {
      setOnChangeHandler(newHandler);
    }
  }, []);
  const SelectComponent = creatable ? /*#__PURE__*/forwardRef((props, ref) => /*#__PURE__*/React.createElement(CreatableSelect, _extends({
    ref: ref,
    formatCreateLabel: input => /*#__PURE__*/React.createElement(FormattedMessage, _extends({}, messages.create, {
      values: {
        input
      }
    }))
  }, props))) : /*#__PURE__*/forwardRef((props, ref) => /*#__PURE__*/React.createElement(Select, _extends({
    ref: ref
  }, props)));
  return /*#__PURE__*/React.createElement(SuggestProvider, {
    changeHandler: onChangeHandler,
    updateOnChangeHandler: updateOnChangeHandler,
    selectRef: temp
  }, /*#__PURE__*/React.createElement(SelectComponent, _extends({
    ref: temp,
    openMenuOnClick: !disabled,
    captureMenuScroll: false,
    classNamePrefix: "suggest-box",
    classes: classes,
    styles: selectorStyles,
    isDisabled: disabled,
    inputId: inputId,
    filterOption: createFilter({
      ignoreAccents: false
    }),
    TextFieldProps: {
      InputLabelProps: {
        htmlFor: inputId
      }
    },
    placeholder: {
      placeholder: placeholder || /*#__PURE__*/React.createElement(FormattedMessage, messages.placeholder),
      tooltipTitle
    },
    components: components,
    options: options,
    value: value,
    onChange: onChangeHandler.onChange,
    isSearchable: true
  }, rest)));
};
export default SuggestBox; // export default withStyles(styles)(SuggestBox);